import React, { useEffect, useState } from 'react';
import { api, todayStr } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Spinner } from '@/components/common';

const emptyForm = { date: todayStr(), spend: '', revenue: '', visitors: '', impressions: '', clicks: '', atc: '', ic: '', purchases: '', notes: '' };

export const MetricDialog = ({ open, onOpenChange, products, defaultProductId, onSaved }) => {
  const [productId, setProductId] = useState(defaultProductId || '');
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setProductId(defaultProductId || '');
      setForm(emptyForm);
    }
  }, [open, defaultProductId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!productId) { toast.error('Select a product'); return; }
    if (!form.date) { toast.error('Pick a date'); return; }
    setBusy(true);
    try {
      const payload = { product_id: productId, date: form.date, notes: form.notes };
      ['spend', 'revenue', 'visitors', 'impressions', 'clicks', 'atc', 'ic', 'purchases'].forEach((k) => {
        payload[k] = form[k] === '' ? 0 : Number(form[k]);
      });
      const { data } = await api.post('/metrics', payload);
      toast.success('Metrics logged — AI diagnosis running in background');
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log metrics');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Log Daily Metrics</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Product *</Label>
              <Select value={productId} onValueChange={setProductId} disabled={!!defaultProductId}>
                <SelectTrigger data-testid="metric-form-product-select" className="mt-1"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>
                  {(products || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date *</Label>
              <Input data-testid="metric-form-date-input" type="date" value={form.date} onChange={set('date')} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[['spend', 'Ad Spend $'], ['revenue', 'Revenue $'], ['impressions', 'Impressions'], ['clicks', 'Link Clicks'], ['visitors', 'Visitors'], ['atc', 'Add to Carts'], ['ic', 'Checkouts (IC)'], ['purchases', 'Purchases']].map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs">{label}</Label>
                <Input data-testid={`metric-form-${k}-input`} type="number" step="0.01" min="0" value={form[k]} onChange={set(k)} placeholder="0" className="mt-1 font-mono tabular-nums" />
              </div>
            ))}
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Input data-testid="metric-form-notes-input" value={form.notes} onChange={set('notes')} placeholder="Anything unusual today?" className="mt-1" />
          </div>
          <p className="text-xs text-muted-foreground">ROAS, CPA, CTR, CPC, CPM, CVR, AOV, funnel rates and profit are computed automatically. AI diagnosis runs right after saving.</p>
          <Button data-testid="metric-form-submit-button" onClick={submit} disabled={busy} className="w-full">
            {busy && <Spinner className="mr-2" />} Save & Diagnose
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
